import { Plus, Trash01 } from '@ethsign/icons';

import { Button, Input, Modal, Select, useToast } from '@ethsign/ui';

import { ISelectProps } from '@ethsign/ui/dist/Select';

import { shortenWalletAddress } from '@ethsign/utils-web';

import { useQuery } from '@tanstack/react-query';

import { ColumnDef } from '@tanstack/react-table';

import { concat } from 'lodash-es';

import React, { useEffect, useRef, useState } from 'react';

import { AddressInfo, Card, ColorRemoveIcon, ColorSearchIcon, EnsInput } from '@/components';
import { EditableTable } from '@/components/Table/EditableTable';
import { AutoComplete } from '@/components/TextField/AutoComplete';
import { NETWORK_TYPE_DISPLAY, NETWORK_TYPE_OPTIONS } from '@/constants';
import { useUserInfo } from '@/hooks';
import { PageCtrl } from '@/pages/prepare/PageCtrl';
import { searchContacts } from '@/services';
import { IContact } from '@/types';
import {
  getEnsAddress,
  getNetworkByAddress,
  getSignerByInputValue,
  isValidEmail,
  WalletType,
  walletValidators
} from '@/utils';
import { useViewModel } from '@/utils/bizify';

import { InviteSignerForTemplate } from './AddSignerCard';
import { formatCurrentUser } from './utils';

const TagSelect = (props: ISelectProps) => {
  // 设置select popover挂载的容器，防止select选择时触发Table保存
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setContainer(ref.current!);
  }, []);

  return (
    <div ref={ref}>
      <Select
        className={'w-[90px] text-xs font-medium rounded-[16px] py-1 h-[26px] bg-gray-100 text-gray-500'}
        container={container}
        {...props}
      />
    </div>
  );
};

export const DeleteRecipientModal = ({
  onConfirm,
  data,
  disabled
}: {
  onConfirm: () => void;
  data: IContact;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Modal
        open={open}
        onOpenChange={setOpen}
        trigger={
          <Button
            disabled={disabled}
            variant={'outline'}
            className="bg-transparent border-none p-0 text-gray-600 cursor-pointer"
          >
            <Trash01 size={20} color={'#475467'} />
          </Button>
        }
        onConfirm={() => {
          setOpen(false);
          onConfirm();
        }}
      >
        <div className="flex items-center flex-col justify-center">
          <ColorRemoveIcon />
          <div className="text-lg font-medium text-gray-900 mt-[20px]">Delete Signer</div>
          <div className="text-sm text-gray-500 mt-[8px] text-center break-all">
            Are you sure you want to delete {data.name || data.email || data.walletAddress} from the contact list? This
            action can not be undone.
          </div>
        </div>
      </Modal>
    </>
  );
};

export const SignersEditTable = ({
  onChange,
  data,
  retainLastOne,
  canEdit = false
}: {
  onChange: (v: IContact[]) => void;
  data: IContact[];
  retainLastOne?: boolean;
  canEdit?: boolean;
}) => {
  const { toast } = useToast();
  const vm = useViewModel(PageCtrl);

  const columns: ColumnDef<IContact>[] = [
    {
      header: 'Name (Optional)',
      accessorKey: 'name',
      meta: {
        renderCell: (record: any) => {
          return record.name ? (
            record.name
          ) : (
            <Button variant="secondary" className={'bg-transparent hover:bg-transparent'}>
              Add Name
            </Button>
          );
        }
      }
    },
    {
      header: 'Email Address',
      accessorKey: 'email',
      meta: {
        canEdit,
        validate: (record, index) => {
          if (record.email && !isValidEmail(record.email)) {
            toast({
              title: 'Please enter a valid email address',
              description: 'Please enter a valid email address so we can send the contract to the right person.',
              variant: 'error'
            });
            return false;
          } else return !vm.isDuplicateRecord(record, index, data, 'email');
        },
        renderCell: (record: any) => {
          return record.email ? (
            record.email
          ) : canEdit ? (
            <Button variant="secondary" className={'bg-transparent hover:bg-transparent'}>
              Add email address
            </Button>
          ) : null;
        },
        renderFormItem: ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
          return (
            <Input
              className={'border-none shadow-none'}
              placeholder="Please input"
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          );
        }
      }
    },
    {
      header: 'Telegram',
      accessorKey: 'telegram',
      meta: {
        renderCell: (record: any) => {
          return record.telegram ? (
            record.telegram
          ) : (
            <Button variant="secondary" className={'bg-transparent hover:bg-transparent px-1'}>
              Add telegram handle
            </Button>
          );
        },
        renderFormItem: ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
          return (
            <Input
              prefix={'@'}
              className={'border-none shadow-none h-full'}
              placeholder="telegram handle"
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          );
        }
      }
    },
    {
      header: 'Wallet Address',
      accessorKey: 'walletAddress',
      meta: {
        canEdit,
        renderCell: (record: any) => {
          return record.walletAddress ? (
            <AddressInfo address={record.walletAddress} network={record.network as WalletType} />
          ) : canEdit ? (
            <Button variant="secondary" className={'bg-transparent hover:bg-transparent'}>
              Add Wallet Address
            </Button>
          ) : null;
        },
        renderFormItem: ({ rowData, setRowData }: { rowData: Record<string, any>; setRowData: (v: any) => void }) => {
          return (
            <div className={'flex gap-2 items-center pr-2'}>
              <EnsInput
                inputClassName={'border-none shadow-none'}
                className={'flex-1'}
                value={rowData.walletAddress}
                onChange={(v) => {
                  const network = getNetworkByAddress(v);
                  setRowData({
                    walletAddress: v,
                    network: network || rowData.network
                  });
                }}
                placeholder={'Please enter wallet address'}
              />
              <TagSelect
                value={rowData.network}
                onChange={(v) => {
                  setRowData({ network: v });
                }}
                placeholder="NetWork"
                options={NETWORK_TYPE_OPTIONS}
                displaySelectValue={(v) => {
                  if (v === WalletType.EVM) {
                    return 'EVM';
                  }
                  return NETWORK_TYPE_DISPLAY[v as WalletType];
                }}
              />
            </div>
          );
        },
        validate: (record: Record<any, any>, index) => {
          if (
            record.walletAddress &&
            record.network &&
            !walletValidators[record.network as WalletType](record.walletAddress)
          ) {
            toast({
              title: 'Please select the correct network',
              description:
                'Please select a network for the wallet address so we can send the contract to the right person.',
              variant: 'error'
            });
            return false;
          } else return !vm.isDuplicateRecord(record, index, data, 'walletAddress');
        }
      }
    },
    {
      header: 'Action',
      meta: {
        canEdit: false,
        renderCell: (record) => {
          // const record = row.original;
          return (
            <div className="flex gap-[12px] items-center text-sm font-medium">
              <DeleteRecipientModal
                data={record}
                onConfirm={() => {
                  const newData = data.filter((i) => i !== record);
                  onChange(newData);
                }}
                disabled={retainLastOne && data.length < 2}
              />
            </div>
          );
        }
      }
    }
  ];

  return (
    <>
      {data?.length ? (
        <EditableTable
          columns={columns}
          data={data as IContact[]}
          onDataChange={(v) => {
            onChange(v);
          }}
          className={'overflow-visible'}
        />
      ) : (
        <div className="flex flex-col items-center py-[40px]">
          <ColorSearchIcon />
          <div className="text-gray-900 font-medium text-md mt-4">No Signer Added</div>
          <div className="text-gray-500 text-sm mt-1 text-center">There is no signer added to the contract.</div>
        </div>
      )}
    </>
  );
};

const AddSignerForContract = (props: { onChange: (v: IContact[]) => void; data: IContact[] }) => {
  const vm = useViewModel(PageCtrl);
  const vmData = vm.$useSnapshot();
  const { data, onChange } = props;
  const { user } = useUserInfo();
  const { toast } = useToast();
  const [options, setOptions] = useState<any[]>([]);
  const { data: contacts } = useQuery({
    queryKey: ['contacts-search'],
    queryFn: () => searchContacts(),
    select: (res) => res.rows
  });
  const [searchValue, setSearchValue] = useState('');

  const renderOptions = options?.map((item) => ({
    label: (
      <div className={'flex items-center justify-between w-full text-sm ml-2'}>
        <div className="text-left">
          <div className="flex flex-row">
            <div className={'max-w-[150px] truncate text-gray-700  font-medium'} title={item.name}>
              {item.name}
            </div>
            <div className={'flex-1 truncate min-w-1 ml-1 text-right text-gray-500'} title={item.email}>
              {item.email}
            </div>
          </div>

          <AddressInfo
            className={'text-gray-500 mt-1'}
            address={item.walletAddress}
            formatAddress={(address) => shortenWalletAddress(address, 'shorter')}
            network={item.network as WalletType}
          />
        </div>
      </div>
    ),
    value: item.walletAddress,
    data: item
  }));

  const getAddressByEns = async (value: string) => {
    const address = await getEnsAddress(value);
    if (address) {
      setOptions([
        {
          walletAddress: address,
          network: WalletType.EVM,
          name: value
        }
      ]);
    } else {
      toast({
        title: 'not found',
        variant: 'error',
        description: 'Can not find any address for the entered ENS name.'
      });
    }
  };

  const addSigners = (newSigners: any[], callback?: () => void) => {
    if (!vm.isValidateSigners(newSigners)) {
      return;
    }
    if (!vm.validateDuplicateNewSigner(data, newSigners)) {
      return;
    }
    const signers = concat(data, newSigners || []);
    onChange(signers);
    callback?.();
  };

  return (
    <div>
      <div className={'mt-8 mb-6'}>
        <div className={'flex flex-row items-center gap-4'}>
          <AutoComplete
            searchValue={searchValue}
            multiple
            value={vmData.signerValue as any[]}
            onChange={(v: any[]) => {
              vm.setSignerValue(v);
            }}
            className={'flex-1'}
            options={renderOptions?.length ? renderOptions : []}
            placeholder={'Enter saved contact’s name, wallet address or email address. Comma separated.'}
            renderTag={(item) => {
              return item.walletAddress ? item.walletAddress : item.email;
            }}
            onSearchValueChange={setSearchValue}
            onSearch={(value) => {
              if (value && value?.endsWith('.eth')) {
                getAddressByEns(value);
              } else {
                setOptions(
                  contacts?.filter((item) => {
                    return [item.name?.toLowerCase(), item.email?.toLowerCase(), item.walletAddress?.toLowerCase()]
                      .filter(Boolean)
                      .some((i) => i.includes(value.toLowerCase()));
                  }) || []
                );
              }
            }}
            formatValue={(v) => {
              return getSignerByInputValue(v);
            }}
          />
          <Button
            variant={'primary'}
            onClick={() => {
              const values = searchValue.split(',').filter(Boolean);
              const newSigners = concat(
                values?.map((v) => getSignerByInputValue(v)),
                vmData.signerValue
              );
              addSigners(newSigners, () => {
                vm.setSignerValue([]);
                if (searchValue) {
                  setSearchValue('');
                }
              });
            }}
            disabled={!vmData.signerValue?.length && !searchValue}
          >
            <Plus color="#FFF" size={20} />
            <span className="ml-2">Add</span>
          </Button>
          <Button
            variant={'outline'}
            className={'flex-[0_0_157px]'}
            onClick={() => {
              const selfSigner = formatCurrentUser(user!);
              addSigners([selfSigner]);
            }}
          >
            <Plus color="#344054" size={20} />
            <span className="ml-2">Add Myself</span>
          </Button>
        </div>
      </div>
      <Card className={'overflow-visible'}>
        <SignersEditTable onChange={onChange} data={data} canEdit />
      </Card>
    </div>
  );
};

export const InviteSigners = (props: { onChange: (v: IContact[]) => void; data: IContact[]; vmData: any }) => {
  const { vmData, onChange, data } = props;
  return (
    <>
      <div className={'mb-8'}>
        <h3 className={'text-gray-900 text-[31px] font-semibold'}>Invite Signers</h3>
        <div className={'text-sm text-gray-600'}>
          Invite signers by email addresses, Telegram Handles, or wallet addresses.
        </div>
      </div>
      {vmData.templateData ? (
        <InviteSignerForTemplate signers={vmData.templateData?.signers} data={data} onChange={onChange} />
      ) : (
        <AddSignerForContract {...props} />
      )}
    </>
  );
};
